/**
 * Reconciliation — wireframe 3 (W2-11): the month's actual work.
 *
 * One read paints everything: the import version, the suggestions (a person
 * accepts, or nothing happens), the stale re-confirmations, both worklists
 * with aging, and the eligible-ITC panel whose every deviation is named.
 */

import { Alert, Button, Card } from '@fiveox/ui'
import { CloudDownload, Play, Scale, Upload } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { FILING, RECON, useEvaluation } from '../lib/access'
import {
  ApiError,
  type ImsRow,
  type ImsStateOut,
  type PortalFetch,
  type ReconState,
  type SuppressedRow,
  type WorklistEntry,
  filing,
  recon,
  reference,
} from '../lib/api'
import { IMS_REASON_REQUIRED, IMS_VS_SUPPRESS, type ImsStateChoice, imsChip, imsStateLabel } from '../lib/ims'
import { describePair, describeSide } from '../lib/matchPair'
import { canResetOnPortal, laneOff, portalImsWords } from '../lib/portalFetch'
import {
  type SupplierLedgerOut,
  headsInWords,
  ledgerSummary,
  nameOf,
  notCountedLabel,
  pairingOf,
  statusLabel,
  statusSentence,
  summaryWords,
  supplierLedgerOf,
  taxOf,
} from '../lib/supplierLedger'
import {
  type TwoAVsTwoB,
  countOf,
  headWords,
  headline,
  rowSentence,
  sectionTitles,
} from '../lib/twoAVsTwoB'
import { useFilingLane } from '../lib/useFilingLane'
import { type WorklistWords, worklistWords } from '../lib/worklistWords'
import { TwoBFileError, twoBFileOf } from '../lib/twoBFile'
import { useClient } from './ClientLayout'

/** The screen's two tabs: the month's reconciliation, and its 2A against its 2B (FR-E28). */
const TABS = [
  { key: 'recon', label: 'Reconciliation' },
  { key: '2a', label: '2A vs 2B' },
] as const
type Tab = (typeof TABS)[number]['key']

/** Indian digit grouping — the legacy renders 6,56,755.08, not 656,755.08. */
function rupees(paise: number | null | undefined): string {
  if (paise === null || paise === undefined) return '—'
  return (paise / 100).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

/**
 * What happens to credit the books claim before the supplier has filed it. It read
 * `ITC where 2B lags: declined —` and then nothing: the service sends the owner's answer, not a
 * `gate`, and the state with its underscore turned to a space stood in for the words (2026-09-29).
 */
const ITC_WHERE_2B_LAGS: Record<string, string> = {
  // W3-18 (4), D-100: the claim is the 2B itself, so there is no "ahead of 2B" left to decide.
  superseded:
    'Credit is claimed as the GSTR-2B carries it; a purchase the register has and the 2B does not waits for the supplier (D-100)',
  declined:
    'Credit is never claimed ahead of GSTR-2B: only invoices the supplier has filed count (the firm’s decision)',
}

export function Recon() {
  const client = useClient()
  const { can } = useEvaluation()

  // The client comes from the URL (ND-C1). This screen used to carry its own
  // picker, which meant the sidebar, the address bar and this dropdown could
  // each name a different client at the same time.
  const clientId = client.id
  const [period, setPeriod] = useState('')
  const [state, setState] = useState<ReconState | null>(null)
  const [reasons, setReasons] = useState<Array<{ key: string; label: string }>>([])
  const [imsReasons, setImsReasons] = useState<Array<{ key: string; label: string }>>([])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [suppressing, setSuppressing] = useState<{ key: string; kind: 'books' | 'tb' } | null>(null)
  const [reason, setReason] = useState('')
  const [tab, setTab] = useState<Tab>('recon')
  const [view, setView] = useState<TwoAVsTwoB | null>(null)
  /** FR-E27: the 2B row whose IMS state is being set, and to what — the reason picker is open for it. */
  const [imsSetting, setImsSetting] = useState<{ key: string; state: ImsStateChoice } | null>(null)
  const [imsReason, setImsReason] = useState('')
  const [imsNote, setImsNote] = useState('')
  const fileInput = useRef<HTMLInputElement>(null)
  /**
   * D-11: the GST portal lane. Built against recorded fixtures, never run against GSTN; with the
   * lane off every portal control is disabled and the screen says why, once.
   */
  const lane = useFilingLane(clientId)
  const off = laneOff(lane)
  const portalReady = !!lane && !off
  /** The last portal call made here, in the service's words — and *Check again* when waiting. */
  const [portalNote, setPortalNote] = useState<PortalFetch | null>(null)
  // Which statement the next picked file is. One picker, one `accept`, two
  // buttons: `tests/slot-claims.test.ts` holds this screen to exactly one
  // accept attribute, so the button pressed decides rather than a second input.
  const importing = useRef<'gstr2b' | 'gstr2a'>('gstr2b')

  const load = useCallback(async () => {
    if (!clientId || !/^\d{2}-\d{4}$/.test(period)) {
      setState(null)
      setView(null)
      return
    }
    try {
      const [stateBody, reasonBody, imsReasonBody, viewBody] = await Promise.all([
        recon.state(clientId, period),
        reference.category('suppress_reason', period),
        reference.category('ims_reason', period),
        recon.twoAVsTwoB(clientId, period),
      ])
      setState(stateBody.data)
      setView(viewBody.data)
      // The label rides each reference row (`reference.py::_SUPPRESS_REASONS`); the list showed the
      // code, underscores turned to spaces (2026-09-29, UI-S9-09).
      const labelled = (values: Array<{ key: string; value: Record<string, unknown> | null }>) =>
        values.map((v) => ({
          key: v.key,
          label: typeof v.value?.label === 'string' ? v.value.label : 'A reason with no words yet',
        }))
      setReasons(labelled(reasonBody.data.values))
      setImsReasons(labelled(imsReasonBody.data.values))
      setError(null)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Reconciliation could not be loaded.')
    }
  }, [clientId, period])

  useEffect(() => {
    void load()
  }, [load])

  const act = useCallback(
    async (fn: () => Promise<unknown>) => {
      setBusy(true)
      setError(null)
      try {
        await fn()
        await load()
      } catch (err) {
        setError(
          err instanceof ApiError || err instanceof TwoBFileError ? err.message : 'That action failed.',
        )
      } finally {
        setBusy(false)
      }
    },
    [load],
  )

  const importFile = useCallback(
    async (files: FileList | null) => {
      const file = files?.[0]
      if (!file || !clientId || !period) return
      const which = importing.current
      await act(async () => {
        if (which === 'gstr2a') {
          // FR-E28: stored beside the 2B, compared on the 2A vs 2B tab, and
          // claimed from by nothing — so matching is not re-run for it.
          await recon.import2a({ file, client_id: clientId, period })
          setTab('2a')
          return
        }
        // The portal hands out 2B as an Excel workbook or as JSON. A file this screen cannot
        // read (an old .xls, anything else) is refused in words before anything is sent
        // (W3-18 (2)). A `.json` goes to the service, which reads the portal's own 2B JSON
        // into the Excel reader's rows (FR-E26, R-E3) as well as ProGST's own rows, and says
        // which it got.
        const kind = twoBFileOf(file.name)
        if (kind.kind === 'refused') throw new TwoBFileError(kind.why)
        if (kind.kind === 'workbook') {
          await recon.importWorkbook({ file, client_id: clientId, period })
        } else {
          await recon.importJson({ file, client_id: clientId, period })
        }
        await recon.run({ client_id: clientId, period })
      })
    },
    [act, clientId, period],
  )

  /** One portal call: its sentence is kept to show, and the month re-read. */
  const portal = useCallback(
    (fn: () => Promise<{ data: PortalFetch }>) =>
      act(async () => {
        setPortalNote((await fn()).data)
      }),
    [act],
  )

  const suppress = useCallback(
    (entry: WorklistEntry, kind: 'books' | 'tb') =>
      act(() =>
        recon.act({
          type: 'suppress',
          client_id: clientId,
          period,
          books_content_key: kind === 'books' ? entry.content_key : undefined,
          tb_row_key: kind === 'tb' ? entry.tb_row_key : undefined,
          reason_code: reason,
        }),
      ).then(() => {
        setSuppressing(null)
        setReason('')
      }),
    [act, clientId, period, reason],
  )

  /**
   * FR-E27: the IMS state — the portal-side decision. Accept and Clear go straight; Reject and
   * Keep pending open the reason picker first, because the service refuses them without one.
   */
  const setIms = useCallback(
    (key: string, imsState: ImsStateChoice, reasonCode?: string, note?: string) =>
      act(() =>
        recon.act({
          type: 'ims',
          client_id: clientId,
          period,
          tb_row_key: key,
          ims_state: imsState,
          reason_code: reasonCode || undefined,
          note: note || undefined,
        }),
      ).then(() => {
        setImsSetting(null)
        setImsReason('')
        setImsNote('')
      }),
    [act, clientId, period],
  )

  /** N8: Suppress had no way back on screen, though the service has always taken a revert. */
  const putBack = useCallback(
    (row: SuppressedRow) =>
      act(() => recon.act({ type: 'revert', client_id: clientId, period, action_id: row.action_id })),
    [act, clientId, period],
  )

  /** D-44: a mistyped register GSTIN joins its 2B twin only when a person says so. */
  const pair = useCallback(
    (booksGstin: string, tbGstin: string) =>
      act(() =>
        recon.act({ type: 'pair', client_id: clientId, period, books_gstin: booksGstin, tb_gstin: tbGstin }),
      ),
    [act, clientId, period],
  )

  const unpair = useCallback(
    (actionId: string) => act(() => recon.act({ type: 'revert', client_id: clientId, period, action_id: actionId })),
    [act, clientId, period],
  )

  const ledger = supplierLedgerOf(state)

  /** The IMS buttons for one 2B row, with the picker open when a reason is wanted. */
  const imsControls = (key: string, current: ImsStateOut | null | undefined) => {
    if (!can(RECON, 'can_update')) return null
    if (imsSetting?.key === key) {
      return (
        <span className="mt-1 flex flex-wrap items-center gap-1">
          <select value={imsReason} onChange={(e) => setImsReason(e.target.value)} className="gst-select">
            <option value="">Reason…</option>
            {imsReasons.map(({ key: code, label }) => (
              <option key={code} value={code}>
                {label}
              </option>
            ))}
          </select>
          <input
            value={imsNote}
            onChange={(e) => setImsNote(e.target.value)}
            placeholder="Note (optional)"
            className="gst-input w-40"
          />
          <Button
            type="button"
            disabled={busy || (IMS_REASON_REQUIRED.has(imsSetting.state) && !imsReason)}
            onClick={() => void setIms(key, imsSetting.state, imsReason, imsNote)}
            variant="outline" size="sm"
          >
            {imsSetting.state === 'rejected' ? 'Reject' : 'Keep pending'}
          </Button>
          <Button type="button" onClick={() => setImsSetting(null)} variant="outline" size="sm">
            Cancel
          </Button>
        </span>
      )
    }
    const is = (s: string) => current?.source === 'user' && current.state === s
    return (
      <span className="mt-1 flex flex-wrap items-center gap-1">
        {!is('accepted') && (
          <Button type="button" disabled={busy} onClick={() => void setIms(key, 'accepted')} variant="outline" size="sm">
            Accept
          </Button>
        )}
        {!is('rejected') && (
          <Button type="button" disabled={busy} onClick={() => setImsSetting({ key, state: 'rejected' })} variant="outline" size="sm">
            Reject…
          </Button>
        )}
        {!is('pending') && (
          <Button type="button" disabled={busy} onClick={() => setImsSetting({ key, state: 'pending' })} variant="outline" size="sm">
            Keep pending…
          </Button>
        )}
        {current?.source === 'user' && current.state !== 'no_action' && (
          <Button type="button" disabled={busy} onClick={() => void setIms(key, 'no_action')} variant="outline" size="sm">
            Clear
          </Button>
        )}
      </span>
    )
  }

  /** FR-E27: the month's IMS rows — states set here, the portal's rejections, and every disagreement. */
  const imsSection = (rows: ImsRow[]) => (
    <div className="mt-6">
      <h3 className="mb-1 text-sm font-medium text-foreground">IMS — what the portal is told ({rows.length})</h3>
      <p className="gst-meta mb-2">{IMS_VS_SUPPRESS}</p>
      {(can(RECON, 'can_update') || can(FILING, 'can_update')) && (
        <div className="mb-2 flex flex-wrap items-center gap-2">
          {can(RECON, 'can_update') && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={busy || !portalReady}
              title={off ?? undefined}
              onClick={() => void portal(() => filing.readIms(clientId, period))}
            >
              Read IMS from the portal
            </Button>
          )}
          {can(FILING, 'can_update') && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={busy || !portalReady}
              title={off ?? 'Send this month’s decisions not yet pushed, with the checksum the portal gave each'}
              onClick={() => void portal(() => filing.pushIms(clientId, period))}
            >
              Push decisions to the portal
            </Button>
          )}
          {off && <span className="gst-meta">{off}</span>}
        </div>
      )}
      {rows.length === 0 ? (
        <div className="gst-empty">No document has an IMS state set here, and the portal’s 2B shows none rejected.</div>
      ) : (
        <div className="gst-table-wrap">
          <table className="gst-table">
            <thead>
              <tr>
                <th className="is-text">Doc</th>
                <th className="is-text">Supplier</th>
                <th className="is-text">State</th>
                <th className="is-text">The portal</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={row.tb_row_key} className={i % 2 === 1 ? 'is-a' : ''}>
                  <td className="is-text is-sec">
                    {row.row?.doc_no ?? 'a row no longer in this month'}
                    <small>{row.row?.doc_date ?? ''}</small>
                  </td>
                  <td className="is-text">
                    <span className="gst-mono">{row.row?.supplier_gstin ?? '—'}</span>
                  </td>
                  <td className="is-text is-sec">
                    <span className="gst-tag">{imsStateLabel(row.state)}</span>
                    <small>
                      {row.reason ?? (row.source === 'portal' ? 'Read off the portal’s own 2B' : 'No reason given')}
                      {row.note ? ` · “${row.note}”` : ''}
                      {row.period_set && row.period_set !== period ? ` · set in ${row.period_set}` : ''}
                    </small>
                    {imsControls(row.tb_row_key, row)}
                  </td>
                  <td className="is-text is-sec">
                    {row.portal === 'rejected'
                      ? 'Rejected on the portal’s 2B'
                      : row.portal === 'carried'
                        ? 'On the portal’s 2B as claimable'
                        : 'Not on the portal’s 2B'}
                    {portalImsWords(row) && <small>{portalImsWords(row)}</small>}
                    {row.disagreement && <small>{row.disagreement}</small>}
                    {can(FILING, 'can_update') && canResetOnPortal(row) && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={busy || !portalReady}
                        title={off ?? 'Set this document back to no action on the portal; ProGST’s decision stays'}
                        onClick={() =>
                          void portal(() => filing.resetIms(clientId, period, [row.tb_row_key]))
                        }
                      >
                        Reset on the portal
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )

  /** W3-09 (e): the supplier ledger 4A was claimed from, in place of the invoice worklists. */
  const supplierSection = (book: SupplierLedgerOut) => {
    const summary = ledgerSummary(book)
    return (
      <div className="space-y-6">
        <div>
          <h3 className="mb-1 text-sm font-medium text-foreground">
            Suppliers ({summary.suppliers})
          </h3>
          <p className="gst-meta mb-2">{summaryWords(summary)}</p>
          <div className="gst-table-wrap">
            <table className="gst-table">
              <thead>
                <tr>
                  <th className="is-text">Supplier</th>
                  <th>Register tax (₹)</th>
                  <th>2B tax (₹)</th>
                  <th>Claimed (₹)</th>
                  <th className="is-text">Status</th>
                </tr>
              </thead>
              <tbody>
                {book.lines.map((line, i) => (
                  <tr key={line.gstin} className={i % 2 === 1 ? 'is-a' : ''}>
                    <td className="is-text is-sec">
                      {line.name ?? 'No name given'}
                      <small className="gst-mono">{line.gstin}</small>
                      {line.gstin_invalid && <span className="gst-tag">GSTIN fails the checksum</span>}
                      {line.joined.map((typed) => {
                        const pairing = pairingOf(book, typed)
                        return (
                          <span key={typed} className="mt-1 flex flex-wrap items-center gap-1 text-xs">
                            <span>
                              Includes <span className="gst-mono">{typed}</span>, as the register typed
                              it — paired by a person
                            </span>
                            {pairing && can(RECON, 'can_update') && (
                              <Button
                                type="button"
                                disabled={busy}
                                onClick={() => void unpair(pairing.action_id)}
                                variant="outline" size="sm"
                              >
                                Undo pairing
                              </Button>
                            )}
                          </span>
                        )
                      })}
                    </td>
                    <td title={headsInWords(line.books)}>{rupees(taxOf(line.books))}</td>
                    <td title={headsInWords(line.tb)}>{rupees(taxOf(line.tb))}</td>
                    <td title={headsInWords(line.eligible)}>{rupees(taxOf(line.eligible))}</td>
                    <td className="is-text is-sec">
                      <span className="gst-tag">{statusLabel(line.status, book.imported !== false)}</span>
                      <small>{statusSentence(line, book)}</small>
                      {line.suggestion && can(RECON, 'can_update') && (
                        <Button
                          type="button"
                          disabled={busy}
                          onClick={() => void pair(line.gstin, line.suggestion!)}
                          variant="outline" size="sm"
                          className="mt-1"
                        >
                          Accept as {line.suggestion}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div>
          <h3 className="mb-1 text-sm font-medium text-foreground">
            Credit notes on the 2B ({book.credit_notes.length})
          </h3>
          {book.credit_notes.length === 0 ? (
            <div className="gst-empty">None this month.</div>
          ) : (
            <>
              <p className="gst-meta mb-2">
                Each reduces its supplier’s claim in this month, booked or not (D-101). Ask the
                client to book any the register does not carry.
              </p>
              <div className="gst-table-wrap">
                <table className="gst-table">
                  <thead>
                    <tr>
                      <th className="is-text">Supplier</th>
                      <th className="is-text">Note</th>
                      <th>Tax (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {book.credit_notes.map((note, i) => (
                      <tr key={`${note.supplier_gstin}|${note.doc_no}|${i}`} className={i % 2 === 1 ? 'is-a' : ''}>
                        <td className="is-text is-sec">
                          {nameOf(book, note.supplier_gstin) ?? 'No name given'}
                          <small className="gst-mono">{note.supplier_gstin}</small>
                        </td>
                        <td className="is-text is-sec">
                          {note.doc_no ?? '—'}
                          <small>{note.doc_date}</small>
                        </td>
                        <td title={headsInWords(note)}>{rupees(Math.abs(taxOf(note)))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {book.not_counted.length > 0 && (
          <div>
            <h3 className="mb-1 text-sm font-medium text-foreground">
              On the 2B and not counted ({book.not_counted.length})
            </h3>
            <ul className="space-y-1 text-sm">
              {book.not_counted.map((row, i) => (
                <li key={`${row.supplier_gstin}|${row.doc_no}|${i}`}>
                  <span className="gst-mono">{row.supplier_gstin || '—'}</span> · {row.doc_no ?? '—'} ·{' '}
                  ₹{rupees(taxOf(row))}
                  <span className="text-muted-foreground"> — {notCountedLabel(row.why)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    )
  }

  const worklistTable = (entries: WorklistEntry[], kind: 'books' | 'tb', words: WorklistWords) => (
    <div className="min-w-0 flex-1">
      <h3 className="mb-2 text-sm font-medium text-foreground">{words.title}</h3>
      {entries.length === 0 ? (
        <div className="gst-empty">{words.empty}</div>
      ) : (
        <div className="gst-table-wrap">
          <table className="gst-table">
            <thead>
              <tr>
                <th className="is-text">Doc</th>
                <th className="is-text">Supplier</th>
                <th>Taxable (₹)</th>
                <th>Age</th>
                <th className="is-text">Next</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry, i) => {
                const key = (kind === 'books' ? entry.content_key : entry.tb_row_key) ?? ''
                return (
                  <tr key={key} className={i % 2 === 1 ? 'is-a' : ''}>
                    <td className="is-text is-sec">
                      {entry.doc_no}
                      <small>{entry.doc_date}</small>
                      {entry.carried_from && (
                        <span className="gst-tag" title="Booked in an earlier month and still waiting for the supplier's 2B (D-38)">
                          booked {entry.carried_from}
                        </span>
                      )}
                      {entry.ims && (
                        <span className="gst-tag" title={entry.ims.note ?? undefined}>
                          {imsChip(entry.ims)}
                        </span>
                      )}
                    </td>
                    <td className="is-text">
                      <span className="gst-mono">{entry.supplier_gstin}</span>
                    </td>
                    <td>{rupees(entry.taxable)}</td>
                    <td>{entry.age_days !== null ? `${entry.age_days}d` : '—'}</td>
                    <td className="is-text">
                      <span title={entry.risk}>{entry.risk}</span>
                      {can(RECON, 'can_update') && (
                        <span className="mt-1 flex flex-wrap items-center gap-1">
                          {suppressing?.key === key ? (
                            <>
                              <select
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                className="gst-select"
                              >
                                <option value="">Reason…</option>
                                {reasons.map(({ key, label }) => (
                                  <option key={key} value={key}>
                                    {label}
                                  </option>
                                ))}
                              </select>
                              <Button
                                type="button"
                                disabled={!reason || busy}
                                onClick={() => void suppress(entry, kind)}
                                variant="outline" size="sm"
                              >
                                Suppress
                              </Button>
                              <Button
                                type="button"
                                onClick={() => setSuppressing(null)}
                                variant="outline" size="sm"
                              >
                                Cancel
                              </Button>
                            </>
                          ) : (
                            <Button
                              type="button"
                              onClick={() => setSuppressing({ key, kind })}
                              variant="outline" size="sm"
                            >
                              Suppress…
                            </Button>
                          )}
                        </span>
                      )}
                      {/* FR-E27: the portal-side decision, beside Suppress (ProGST's own). */}
                      {kind === 'tb' && imsControls(key, entry.ims)}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )

  return (
    <div>
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="gst-h1">
            <Scale size={20} /> Reconciliation
          </h1>
          <p className="gst-sub">
            Books vs 2B — every rupee of ITC traces to a match, an action, or a named gate
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <label className="gst-label">
            Period
            <input
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              placeholder="07-2026"
              className="gst-input w-24"
            />
          </label>
          {can(RECON, 'can_create') && (
            <>
              <Button
                type="button"
                disabled={!clientId || !period || busy}
                onClick={() => {
                  importing.current = 'gstr2b'
                  fileInput.current?.click()
                }}
                size="sm"
              >
                <Upload size={15} /> Import 2B
              </Button>
              <Button
                type="button"
                disabled={!clientId || !period || busy}
                onClick={() => {
                  importing.current = 'gstr2a'
                  fileInput.current?.click()
                }}
                variant="outline" size="sm"
                title="The portal's GSTR-2A, .xlsx or .json — stored beside the 2B and compared with it; never claimed from"
              >
                <Upload size={15} /> Import 2A
              </Button>
              <Button
                type="button"
                disabled={!clientId || !period || busy || !portalReady}
                onClick={() => void portal(() => filing.fetch2b(clientId, period))}
                variant="outline" size="sm"
                title={off ?? 'The portal’s GSTR-2B for this month, imported as its JSON download is'}
              >
                <CloudDownload size={15} /> Fetch 2B from the portal
              </Button>
              <Button
                type="button"
                disabled={!clientId || !period || busy || !portalReady}
                onClick={() => void portal(() => filing.generate2b(clientId, period))}
                variant="outline" size="sm"
                title={off ?? 'Ask the portal to generate this month’s GSTR-2B now'}
              >
                Generate 2B now
              </Button>
              <input
                ref={fileInput}
                type="file"
                accept=".xlsx,application/json,.json"
                hidden
                onChange={(e) => {
                  void importFile(e.target.files)
                  e.target.value = ''
                }}
              />
            </>
          )}
          {/* Matching pairs invoices; a party-wise month has none to pair. */}
          {can(RECON, 'can_update') && state?.import && !ledger && (
            <Button
              type="button"
              disabled={busy}
              onClick={() => void act(() => recon.run({ client_id: clientId, period }))}
              variant="outline" size="sm"
            >
              <Play size={15} /> Re-run matching
            </Button>
          )}
        </div>
      </header>

      {error && <Alert tone="error" className="mb-4">{error}</Alert>}
      {off && <p className="gst-meta mb-4">The GST portal controls here are off: {off}</p>}
      {portalNote && (
        <Alert tone="info" className="mb-4">
          {portalNote.what}: {portalNote.sentence}
          {portalNote.next?.action === 'check' && (
            <>
              {' '}
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={() => void portal(() => filing.check(clientId, portalNote.id))}
              >
                {portalNote.next.label}
              </Button>
            </>
          )}
        </Alert>
      )}

      {state && (
        <div className="gst-tabs mb-4">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              className="gst-tab"
              aria-selected={tab === t.key}
              onClick={() => setTab(t.key)}
            >
              {t.label}
              {t.key === '2a' && countOf(view) > 0 && (
                <span className="gst-count">{countOf(view)}</span>
              )}
            </button>
          ))}
        </div>
      )}

      {!state ? (
        <p className="gst-sub">Pick a client and a period (MM-YYYY).</p>
      ) : tab === '2a' ? (
        <div className="space-y-6">
          <p className="gst-sub">{headline(view, period)}</p>
          {view?.state === 'compared' &&
            sectionTitles(view).map(({ key, title }) => {
              const rows = view[key] ?? []
              return (
                <div key={key}>
                  <h3 className="mb-2 text-sm font-medium text-foreground">{title}</h3>
                  {rows.length === 0 ? (
                    <div className="gst-empty">None.</div>
                  ) : (
                    <ul className="list-disc space-y-2 pl-4 text-sm">
                      {rows.map((row) => (
                        <li key={row.key}>
                          {rowSentence(row)}
                          {key === 'differences' && (
                            <span className="block text-xs text-muted-foreground">
                              {headWords(row).join('; ')}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )
            })}
        </div>
      ) : (
        <>
          {ledger && (
            <Alert tone="info" className="mb-4">
              <p className="gst-panel-h">Reconciled supplier by supplier</p>
              {state.party_wise.length > 0 ? (
                <>{state.party_wise.join(', ')} lists</>
              ) : (
                <>This month’s purchase register lists</>
              )}{' '}
              suppliers, not invoices, so the register is compared with the 2B supplier by
              supplier. Each supplier is claimed as its 2B carries it. Invoice matching does not
              apply to this month.
            </Alert>
          )}

          {/* ITC panel — the month's answer, deviations named. The legacy states
              the rule on the panel; so does this. */}
          <p className="gst-sub mb-2">
            {ledger
              ? 'CLAIMABLE ITC = the GSTR-2B, supplier by supplier, less rows marked ITC not available'
              : 'CLAIMABLE ITC = the GSTR-2B, less rows marked ITC not available'}
          </p>
          <div className="gst-stats mb-6">
            {(['igst', 'cgst', 'sgst', 'cess'] as const).map((head) => (
              <div key={head} className="gst-stat">
                <div className="n is-money">₹{rupees(state.itc.eligible[head] ?? 0)}</div>
                <div className="l">{head.toUpperCase()} ELIGIBLE</div>
                {(state.itc.held_stale[head] ?? 0) > 0 && (
                  <p className="text-[11px] text-muted-foreground">
                    + {rupees(state.itc.held_stale[head])} held (stale — re-confirm)
                  </p>
                )}
                {(state.itc.disputed[head] ?? 0) > 0 && (
                  <p className="text-[11px] text-muted-foreground">
                    {rupees(state.itc.disputed[head])} disputed
                  </p>
                )}
              </div>
            ))}
          </div>

          <p className="gst-meta mb-6">
            {state.import
              ? `2B v${state.import.version_no} · ${state.import.row_count} rows · ` +
                `+${state.import.diff.added ?? 0} / −${state.import.diff.removed ?? 0} / ~${state.import.diff.changed ?? 0} vs previous`
              : 'No 2B imported for this period yet.'}
            {' · '}
            {ITC_WHERE_2B_LAGS[state.itc.fallback.state] ??
              'Credit where GSTR-2B lags: not decided'}
          </p>

          {/* suggestions + stale — a person decides */}
          {!ledger && (state.suggested.length > 0 || state.stale.length > 0) && can(RECON, 'can_update') && (
            <Alert tone="warning" className="mb-6">
              <p className="gst-panel-h">Waiting on you — nothing matches itself</p>
              <ul className="space-y-1">
                {[...state.suggested, ...state.stale].map((match) => {
                  const pair = describePair(match)
                  return (
                  <li key={match.id} className="flex flex-wrap items-center gap-2 text-sm text-foreground">
                    <span>
                      {match.status === 'stale' ? 'Re-confirm' : 'Suggested'} ·{' '}
                      {match.rung}
                      {match.evidence?.score !== undefined &&
                        ` (score ${String(match.evidence.score)})`}
                      {pair.supplier && (
                        <span className="gst-mono"> · {pair.supplier}</span>
                      )}
                      <span className="block text-xs">Books: {pair.books}</span>
                      <span className="block text-xs">2B: {pair.tb}</span>
                      {pair.differs.length > 0 && (
                        <span className="block text-xs text-muted-foreground">
                          Differs in: {pair.differs.join(', ')}
                        </span>
                      )}
                    </span>
                    <Button
                      type="button"
                      disabled={busy}
                      onClick={() =>
                        void act(() =>
                          recon.act({
                            type: 'accept',
                            client_id: clientId,
                            period,
                            match_id: match.id,
                          }),
                        )
                      }
                      variant="outline" size="sm"
                    >
                      {match.status === 'stale' ? 'Re-confirm' : 'Accept'}
                    </Button>
                  </li>
                  )
                })}
              </ul>
            </Alert>
          )}

          {state.books_notes.length > 0 && (
            <Card className="mb-4 p-3 text-xs text-muted-foreground">
              {/* Each note is a different kind of thing now — a row skipped, rows
                  held in review, purchases carried from earlier months, rows past
                  the time limit — so they are listed, not summarised as "skipped". */}
              <ul className="list-disc space-y-1 pl-4">
                {state.books_notes.slice(0, 5).map((note) => (
                  <li key={note}>{note}</li>
                ))}
              </ul>
              {state.books_notes.length > 5 && (
                <p className="mt-1">and {state.books_notes.length - 5} more</p>
              )}
            </Card>
          )}

          {ledger ? (
            supplierSection(ledger)
          ) : (
            <div className="flex flex-wrap gap-6">
              {worklistTable(state.worklists.books_only, 'books', worklistWords('books', state.import != null))}
              {worklistTable(state.worklists.tb_only, 'tb', worklistWords('tb', state.import != null))}
            </div>
          )}

          {imsSection(state.worklists.ims ?? [])}

          {(state.worklists.suppressed ?? []).length > 0 && (
            <div className="mt-6">
              <h3 className="mb-2 text-sm font-medium text-foreground">
                Taken off the worklist ({state.worklists.suppressed!.length})
              </h3>
              <ul className="space-y-2">
                {state.worklists.suppressed!.map((s) => (
                  <li key={s.action_id} className="flex flex-wrap items-baseline gap-2 text-sm">
                    <span>
                      {s.side === 'books' ? 'Books' : 'GSTR-2B'}:{' '}
                      {s.row ? describeSide(s.row) : 'a row no longer in this month'}
                    </span>
                    <span className="text-muted-foreground">
                      — {s.reason}
                      {s.note ? ` · “${s.note}”` : ''}
                    </span>
                    {can(RECON, 'can_update') && (
                      <Button type="button" variant="outline" size="sm" onClick={() => void putBack(s)}>
                        Put back
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  )
}
